import { Container, Graphics, Sprite, Texture, Application } from 'pixi.js';

interface GoldStar {
    x: number;
    y: number;
    sprite: Sprite;
    collected: boolean;
    rotation: number;
    index: number;
}

export default class GoldStarManager {
    private app: Application;
    private gameWorld: Container;
    private stars: GoldStar[] = [];
    private starTexture: Texture | null = null;

    constructor(app: Application, gameWorld: Container) {
        this.app = app;
        this.gameWorld = gameWorld;
        this.createStarTexture();
    }

    private createStarTexture(): void {
        const graphics = new Graphics();
        const radius = 20;
        const spikes = 5;
        
        // Draw star shape
        graphics.moveTo(0, -radius);
        for (let i = 0; i < spikes * 2; i++) {
            const angle = (i * Math.PI) / spikes;
            const r = i % 2 === 0 ? radius : radius * 0.5;
            const x = Math.sin(angle) * r;
            const y = -Math.cos(angle) * r;
            graphics.lineTo(x, y);
        }
        graphics.closePath();
        graphics.fill({ color: 0xFFD700, alpha: 0.9 });
        graphics.stroke({ width: 2, color: 0xFFA500 });
        
        this.starTexture = this.app.renderer.generateTexture(graphics);
    }

    public createStar(x: number, y: number, index: number): void {
        if (!this.starTexture) {
            this.createStarTexture();
        }

        const sprite = new Sprite(this.starTexture);
        sprite.anchor.set(0.5);
        sprite.x = x;
        sprite.y = y;
        sprite.scale.set(1);
        
        this.gameWorld.addChild(sprite);

        this.stars.push({
            x,
            y,
            sprite,
            collected: false,
            rotation: 0,
            index
        });
    }

    public collectStar(index: number): GoldStar | null {
        const star = this.stars.find(s => s.index === index && !s.collected);
        if (star) {
            star.collected = true;
            return star;
        }
        return null;
    }

    public update(delta: number, targetPosition?: {x: number, y: number}, magnetRadius: number = 0): void {
        this.stars.forEach((star, index) => {
            if (!star.collected) {
                // Magnet attraction
                if (targetPosition && magnetRadius > 0) {
                    const dx = targetPosition.x - star.x;
                    const dy = targetPosition.y - star.y;
                    const distSq = dx * dx + dy * dy;
                    
                    if (distSq < magnetRadius * magnetRadius) {
                        const dist = Math.sqrt(distSq);
                        const speed = 15 * (1 - dist / magnetRadius) + 5;
                        star.x += (dx / dist) * speed * delta;
                        star.y += (dy / dist) * speed * delta;
                        star.sprite.x = star.x;
                        star.sprite.y = star.y;
                    }
                }

                // Rotate the star
                star.rotation += delta * 0.03;
                star.sprite.rotation = star.rotation;
                
                // Pulse effect
                const pulse = 1 + Math.sin(Date.now() * 0.004 + index) * 0.2;
                star.sprite.scale.set(pulse);
                
                // Glow effect
                const alpha = 0.8 + Math.sin(Date.now() * 0.006 + index) * 0.2;
                star.sprite.alpha = alpha;
            } else {
                // Animate collection with sparkle
                star.sprite.scale.x *= 0.85;
                star.sprite.scale.y *= 0.85;
                star.sprite.rotation += delta * 0.2;
                star.sprite.alpha *= 0.7;
                
                if (star.sprite.alpha < 0.1) {
                    this.gameWorld.removeChild(star.sprite);
                    this.stars.splice(this.stars.indexOf(star), 1);
                }
            }
        });
    }

    public removeStar(index: number): void {
        const star = this.stars.find(s => s.index === index);
        if (star && star.sprite.parent) {
            this.gameWorld.removeChild(star.sprite);
            this.stars.splice(this.stars.indexOf(star), 1);
        }
    }

    public clearAll(): void {
        this.stars.forEach(star => {
            if (star.sprite.parent) {
                this.gameWorld.removeChild(star.sprite);
            }
        });
        this.stars = [];
    }

    public getStars(): GoldStar[] {
        return this.stars;
    }
}

